"use client";

import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
  useEffect,
} from "react";
import type { InvoiceData } from "@/app/schema";
import { STORAGE_KEYS, migrateStorageKeys } from "@/lib/storage-keys";

interface CachedPDF {
  blob: Blob;
  url: string;
  hash: string;
  timestamp: number;
  invoiceData: InvoiceData;
}

interface PDFCacheContextType {
  // Get cached PDF if it matches the current data hash
  getCachedPDF: (hash: string) => CachedPDF | null;
  // Store a new PDF in cache
  setCachedPDF: (hash: string, blob: Blob, invoiceData: InvoiceData) => string;
  // Clear the cache
  clearCache: () => void;
  // Check if generation is needed
  shouldRegenerate: (hash: string) => boolean;
  // Last generation time for rate limiting
  lastGenerationTime: number;
  updateLastGenerationTime: () => void;
}

const PDFCacheContext = createContext<PDFCacheContextType | null>(null);

// Storage keys for session persistence - using secure naming
const PDF_HASH_KEY = STORAGE_KEYS.PDF_CACHE_HASH;
const PDF_TIMESTAMP_KEY = STORAGE_KEYS.PDF_CACHE_TIMESTAMP;

export function PDFCacheProvider({ children }: { children: React.ReactNode }) {
  // Use ref for fast synchronous access
  const cacheRef = useRef<Map<string, CachedPDF>>(new Map());
  const lastGenTimeRef = useRef<number>(0);
  const [mounted, setMounted] = useState(false);

  // Restore cache from sessionStorage on mount
  useEffect(() => {
    setMounted(true);
    if (typeof window === "undefined") return;

    // Migrate any legacy storage keys on startup
    migrateStorageKeys();

    try {
      const cachedHash = sessionStorage.getItem(PDF_HASH_KEY);
      const cachedTimestamp = sessionStorage.getItem(PDF_TIMESTAMP_KEY);

      if (cachedHash && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        // Only restore if less than 30 minutes old
        if (Date.now() - timestamp < 30 * 60 * 1000) {
          lastGenTimeRef.current = timestamp;
        }
      }
    } catch {
      // Ignore storage errors
    }

    // Cleanup on unmount
    return () => {
      clearCache();
    };
  }, []);

  const getCachedPDF = useCallback((hash: string): CachedPDF | null => {
    const cached = cacheRef.current.get(hash);
    if (!cached) return null;

    // Check if cache is still valid (30 minute TTL)
    if (Date.now() - cached.timestamp > 30 * 60 * 1000) {
      cacheRef.current.delete(hash);
      return null;
    }

    return cached;
  }, []);

  const setCachedPDF = useCallback(
    (hash: string, blob: Blob, invoiceData: InvoiceData): string => {
      // Revoke old URL for this hash if exists
      const existing = cacheRef.current.get(hash);
      if (existing) {
        URL.revokeObjectURL(existing.url);
      }

      // Create new URL
      const url = URL.createObjectURL(blob);

      const cached: CachedPDF = {
        blob,
        url,
        hash,
        timestamp: Date.now(),
        invoiceData,
      };

      cacheRef.current.set(hash, cached);
      lastGenTimeRef.current = Date.now();

      // Store in sessionStorage for persistence
      if (typeof window !== "undefined") {
        try {
          sessionStorage.setItem(PDF_HASH_KEY, hash);
          sessionStorage.setItem(PDF_TIMESTAMP_KEY, Date.now().toString());
        } catch {
          // Ignore storage errors
        }
      }

      return url;
    },
    [],
  );

  const clearCache = useCallback(() => {
    // Revoke all URLs
    cacheRef.current.forEach((cached) => {
      URL.revokeObjectURL(cached.url);
    });
    cacheRef.current.clear();

    if (typeof window !== "undefined") {
      try {
        sessionStorage.removeItem(PDF_HASH_KEY);
        sessionStorage.removeItem(PDF_TIMESTAMP_KEY);
      } catch {
        // Ignore storage errors
      }
    }
  }, []);

  const shouldRegenerate = useCallback(
    (hash: string): boolean => {
      // Check if we have a valid cached version
      const cached = getCachedPDF(hash);
      if (cached) return false;

      // Rate limiting: prevent generation more than once per 100ms
      const timeSinceLastGen = Date.now() - lastGenTimeRef.current;
      if (timeSinceLastGen < 100) {
        return false;
      }

      return true;
    },
    [getCachedPDF],
  );

  const updateLastGenerationTime = useCallback(() => {
    lastGenTimeRef.current = Date.now();
  }, []);

  // Don't render until mounted to avoid hydration issues
  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <PDFCacheContext.Provider
      value={{
        getCachedPDF,
        setCachedPDF,
        clearCache,
        shouldRegenerate,
        lastGenerationTime: lastGenTimeRef.current,
        updateLastGenerationTime,
      }}
    >
      {children}
    </PDFCacheContext.Provider>
  );
}

export function usePDFCache() {
  const context = useContext(PDFCacheContext);
  if (!context) {
    throw new Error("usePDFCache must be used within PDFCacheProvider");
  }
  return context;
}
